import { useCallback, useEffect, useMemo, useState } from 'react';

import InscritoService from '../../../sdk/services/InscritoService';
import ResolucaoService from '../../../sdk/services/ResolucaoService';
import ReviewService from '../../../sdk/services/ReviewService';

import type { CityBundle, FinishedMocktest, ResolutionRow } from './types';

export type EndpointStatus = 'idle' | 'ok' | 'error';

export type UseAcerteiCityDataOptions = {
  /**
   * Baixa `/resolutions/byuser` inteiro — é o que dá "Hoje", a sequência e a
   * evolução de 7 dias por matéria. Sem ele a cidade some com esses três e
   * escreve a nota. Padrão `true`.
   *
   * ⚠️ Essa rota não tem filtro de data nem paginação: um aluno antigo baixa o
   * histórico todo a cada montagem. É o mesmo custo que o `/home` já paga para
   * o heatmap e a sequência dele.
   */
  includeHistory?: boolean;
  /** Relê `/review/stats` (leitura pura) a cada N ms. `0` desliga. Padrão 60s. */
  pollMs?: number;
};

/**
 * Busca, em paralelo, as leituras que a cidade desenha.
 *
 * Decisões que importam:
 *
 * - **Usa os serviços do projeto, não `fetch` cru.** A versão colada em
 *   `_incoming/` trazia `baseUrl` + `getToken: () => localStorage.getItem('token')`
 *   e montava o header na mão. Aqui as cinco leituras já existem como método de
 *   serviço, e passar pelo axios de `sdk/services/Service.ts` é o que dá
 *   `Authorization` automático e o interceptor de negativa de
 *   `app/auth/httpConfig.ts` — inclusive a regra de que assinatura vencida
 *   chega como 401 e **não** desloga.
 * - **`Promise.allSettled`, nunca `all`.** Cada leitura falha sozinha: a cidade
 *   recebe o que deu certo e anota o que não deu. Um 403 numa delas não apaga
 *   as outras quatro.
 * - **Não lê mais `/registereds/allranked/:id`.** A carreira passou a sair do
 *   percentual do ÚLTIMO simulado finalizado (`lastFinished`, última posição do
 *   array), e não de uma colocação: o id de simulado que aquela rota exige não
 *   existe neste payload — `lastfinished` devolve o id da *inscrição* —, então
 *   a posição só era obtida perguntando ao próprio aluno em qual lugar ele
 *   ficou, o que fazia do pódio uma ficção. Quem decide o nível é
 *   `careerTier()`, dentro da cidade.
 * - **Nunca chama `/review/due` nem `/review/forecast`.** As duas materializam
 *   os modelos de flashcard como efeito de leitura, então não podem rodar num
 *   painel que monta em toda visita e ainda repete a cada 60s. `/review/stats`
 *   é leitura pura e entrega os mesmos números.
 */
export function useAcerteiCityData(opts: UseAcerteiCityDataOptions = {}) {
  const { includeHistory = true, pollMs = 60_000 } = opts;

  const [bundle, setBundle] = useState<CityBundle | null>(null);
  const [status, setStatus] = useState<Record<string, EndpointStatus>>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);

    const jobs: Record<string, Promise<unknown>> = {
      reviewStats: ReviewService.getStats(),
      bySubject: ResolucaoService.getSubjectHistory(),
      lastFinished: InscritoService.getLastFinished() as Promise<FinishedMocktest[]>,
    };
    if (includeHistory) {
      jobs.byUser = ResolucaoService.getResolucoes() as unknown as Promise<ResolutionRow[]>;
    }

    const keys = Object.keys(jobs);
    const results = await Promise.allSettled(keys.map((k) => jobs[k]));

    const next: CityBundle = {};
    const st: Record<string, EndpointStatus> = {};
    results.forEach((r, i) => {
      const k = keys[i];
      st[k] = r.status === 'fulfilled' ? 'ok' : 'error';
      if (r.status === 'fulfilled') (next as Record<string, unknown>)[k] = r.value;
      else console.warn('[acertei-city]', k, r.reason);
    });

    setStatus(st);
    setBundle(next);
    setLoading(false);
  }, [includeHistory]);

  useEffect(() => {
    let cancelled = false;
    // `load` já guarda o resultado no estado; o guard evita o set depois do
    // unmount sem precisar de AbortController (o axios do projeto não expõe um).
    void load().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  // Atualização leve: só as estatísticas de revisão, que são leitura pura. O
  // limite é 10 req/s por credencial, então 60s não encosta nele.
  useEffect(() => {
    if (!pollMs) return;
    const id = setInterval(() => {
      ReviewService.getStats()
        .then((reviewStats) => {
          setBundle((b) => ({ ...(b ?? {}), reviewStats }));
        })
        .catch(() => {
          /* mantém o último valor bom */
        });
    }, pollMs);
    return () => clearInterval(id);
  }, [pollMs]);

  return useMemo(() => ({ bundle, status, loading, reload: load }), [bundle, status, loading, load]);
}

export default useAcerteiCityData;
